import { useRef, useSyncExternalStore } from 'react';
import { RoiId } from '~/domain/changes/ids';
import { createRoiDetailView } from '~/domain/projections/factories';
import { RoiDetailView } from '~/domain/projections/views';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import { useDomainStore } from './useDomainStore';
import { usePatient } from './usePatient';

interface CacheEntry {
    version: number;
    roiId: RoiId;
    view: RoiDetailView | undefined;
}

export function useRoiDetailView(
    id: RoiId | undefined
): RoiDetailView | undefined {
    const store = useDomainStore();
    const patient = usePatient();
    const cacheRef = useRef<CacheEntry | null>(null);

    return useSyncExternalStore(store.subscribe, () => {
        if (id === undefined) {
            return undefined;
        }
        const version = store.getVersion();
        if (
            cacheRef.current &&
            cacheRef.current.version === version &&
            cacheRef.current.roiId === id
        ) {
            return cacheRef.current.view;
        }
        let view: RoiDetailView | undefined;
        for (const ss of patient.structureSets) {
            const roi = ss.getRoiById(id);
            if (roi) {
                view = createRoiDetailView(roi);
                break;
            }
        }
        if (view === undefined) {
            domainTrace.view('useRoiDetailView not-found', {
                id,
                version,
                status: 'not-found',
            });
        } else {
            domainTrace.view('useRoiDetailView miss', { id, version });
        }
        cacheRef.current = { version, roiId: id, view };
        return view;
    });
}
